import { createPublicClient, fallback, http, PublicClient } from 'viem';
import { mainnet, arbitrum, base } from 'viem/chains';

/**
 * Custom Ink chain definition for viem.
 * Ink is not in the standard viem/chains export, so we define it manually.
 */
export const inkChain = {
  id: 57073,
  name: 'Ink',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc-gel.inkonchain.com'] },
    public: { http: ['https://rpc-gel.inkonchain.com'] },
  },
  blockExplorers: {
    default: {
      name: 'Ink Explorer',
      url: 'https://explorer.inkonchain.com',
    },
  },
} as const;

/** RPC URLs per chain ID, ordered by preference. */
const RPC_URLS: Record<number, readonly string[]> = {
  1: ['https://ethereum.publicnode.com', 'https://eth.llamarpc.com'],
  42161: ['https://arb1.arbitrum.io/rpc'],
  8453: ['https://mainnet.base.org'],
  57073: ['https://rpc-gel.inkonchain.com'],
};

/** viem chain objects per chain ID */
const VIEM_CHAINS: Record<number, typeof mainnet | typeof arbitrum | typeof base | typeof inkChain> = {
  1: mainnet,
  42161: arbitrum,
  8453: base,
  57073: inkChain,
};

/** Cache of publicClient instances to avoid recreating them on every call */
const clientCache: Map<number, PublicClient> = new Map();

/**
 * Returns a viem PublicClient for the given EVM chain ID.
 *
 * Clients are cached by chainId — calling this function multiple times
 * with the same chainId returns the same instance.
 *
 * Supported chains: Ethereum (1), Arbitrum One (42161), Base (8453), Ink (57073).
 *
 * @throws if chainId is not a supported source chain.
 */
export function getSourceChainPublicClient(chainId: number): PublicClient {
  const cached = clientCache.get(chainId);
  if (cached) return cached;

  const rpcUrls = RPC_URLS[chainId];
  const chain = VIEM_CHAINS[chainId];

  if (!rpcUrls?.length || !chain) {
    throw new Error(
      `getSourceChainPublicClient: unsupported chainId ${chainId}. Supported: ${Object.keys(RPC_URLS).join(', ')}`,
    );
  }

  const transport =
    rpcUrls.length === 1
      ? http(rpcUrls[0])
      : fallback(rpcUrls.map((rpcUrl) => http(rpcUrl)));

  const client = createPublicClient({
    chain,
    transport,
  });

  const publicClient = client as unknown as PublicClient;
  clientCache.set(chainId, publicClient);
  return publicClient;
}

/**
 * Clears the client cache. Useful for testing.
 */
export function clearSourceChainClientCache(): void {
  clientCache.clear();
}
